import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import InventoryPage from './pages/inventory/InventoryPage';
import InventoryDetailPage from './pages/inventory/InventoryDetailPage';
import ApiKeysPage from './pages/api-keys/ApiKeysPage';
import UsersPage from './pages/users/UsersPage';
import ReportsPage from './pages/reports/ReportsPage';
import UsageDashboardPage from './pages/dashboard/UsageDashboardPage';
import WarehousesPage from './pages/warehouses/WarehousesPage';
import AddOnsPage from './pages/addons/AddOnsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ProfilePage from './pages/settings/ProfilePage';
import SignupPage from './pages/signup/SignupPage';
import { useAuthStore } from './store/authStore';
import { ReadOnlyProvider } from './context/ReadOnlyContext';

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';
const USER_POOL_CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
const APP_URL = process.env.REACT_APP_URL || window.location.origin;

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
      loginWith: {
        email: true,
        oauth: COGNITO_DOMAIN ? {
          domain: COGNITO_DOMAIN,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: [`${APP_URL}/auth/callback`, `${APP_URL}/signup/callback`],
          redirectSignOut: [`${APP_URL}/login`],
          responseType: 'code',
        } : undefined,
      },
    },
  },
});

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiPaper: { defaultProps: { elevation: 0 } },
  },
});

export default function App() {
  // Public routes (signup) bypass Authenticator
  const path = window.location.pathname;
  if (path.startsWith('/signup')) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/*" element={<SignupPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Authenticator hideSignUp={true}>
        {({ signOut, user }) => (
          <AuthenticatedApp user={user} signOut={signOut} />
        )}
      </Authenticator>
    </ThemeProvider>
  );
}

function AuthenticatedApp({ user, signOut }) {
  const { setUser } = useAuthStore();

  useEffect(() => {
    if (user) { setUser(user); }
  }, [user, setUser]);

  return (
    <ReadOnlyProvider>
    <BrowserRouter>
      <AppLayout onSignOut={signOut} user={user}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/usage" element={<UsageDashboardPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/inventory/:itemId" element={<InventoryDetailPage />} />
          <Route path="/warehouses" element={<WarehousesPage />} />
          <Route path="/api-keys" element={<ApiKeysPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/add-ons" element={<AddOnsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AppLayout>
    </BrowserRouter>
    </ReadOnlyProvider>
  );
}
